/**
 * Logica pura dei comandi del Lead/Cliente nel pannello chat (fase pipeline,
 * pipeline, stato, assegnatario). Stesse regole della scheda lead
 * (LeadManagementRows) e del kanban: con pipeline si scrive pipeline_stage_id,
 * senza pipeline lead_stage; spostando in una pipeline si parte dalla prima fase.
 */
import type { LeadStage } from '@/lib/types';
import { friendlyDbError } from '@/lib/utils/friendlyDbError';

export interface StageLike {
  id: string;
  name: string;
  color?: string | null;
  sort_order?: number | null;
  is_active?: boolean;
  is_won?: boolean;
  is_lost?: boolean;
}

export interface ClientStateLike {
  id: string;
  name: string;
  color?: string | null;
  is_active?: boolean;
  is_lead?: boolean;
}

export interface OptionLike {
  value: string;
  label: string;
  color?: string | null;
  description?: string | null;
}

/** Fasi (della pipeline o, senza pipeline, gli stati lead) nel formato LeadStage. */
export function toLeadStages(stages: StageLike[]): LeadStage[] {
  return stages
    .filter((s) => s.is_active !== false)
    .map((s, index) => ({
      id: s.id,
      name: s.name,
      color: s.color || '#6B7280',
      order: s.sort_order || index + 1,
      is_won: s.is_won,
      is_lost: s.is_lost,
    }));
}

export function stageOptions(stages: LeadStage[]): OptionLike[] {
  return stages.map((s) => ({ value: s.id, label: s.name, color: s.color }));
}

/** Stati (client_states) attivi, con "Lead"/"Cliente" come descrizione (come nella scheda). */
export function clientStateOptions(states: ClientStateLike[]): OptionLike[] {
  return states
    .filter((s) => s.is_active !== false)
    .map((s) => ({ value: s.id, label: s.name, color: s.color, description: s.is_lead ? 'Lead' : 'Cliente' }));
}

/** Fase attuale: pipeline_stage_id con pipeline, lead_stage senza. */
export function currentStageId(lead: { pipeline_id?: string | null; pipeline_stage_id?: string | null; lead_stage?: string | null }): string | null {
  return (lead.pipeline_id ? lead.pipeline_stage_id : lead.lead_stage) || null;
}

/** Dati da scrivere per spostare di fase (stesso contratto di LeadManagementRows). */
export function stageUpdate(hasPipeline: boolean, stageId: string, now: Date = new Date()) {
  return hasPipeline
    ? { pipeline_stage_id: stageId, last_activity_date: now.toISOString() }
    : { lead_stage: stageId, last_activity_date: now.toISOString() };
}

/**
 * Prima fase in cui mettere un lead che entra in una pipeline: la prima in
 * ordine che non sia vinta o persa (se ci sono solo quelle, la prima).
 */
export function firstStageId(stages: StageLike[]): string | null {
  const active = stages.filter((s) => s.is_active !== false);
  const sorted = [...active].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const open = sorted.find((s) => !s.is_won && !s.is_lost);
  return (open || sorted[0])?.id || null;
}

/** Dati da scrivere per mettere il lead in una pipeline; null se la pipeline non ha fasi. */
export function pipelineUpdate(pipelineId: string, stages: StageLike[], now: Date = new Date()) {
  const stageId = firstStageId(stages);
  if (!stageId) return null;
  return { pipeline_id: pipelineId, pipeline_stage_id: stageId, lead_stage: null, last_activity_date: now.toISOString() };
}

/** Pipeline selezionabili: solo quelle vere (non la voce virtuale "Senza pipeline"). */
export function pipelineOptions(pipelines: { id: string; name: string; color?: string | null; is_default?: boolean }[]): OptionLike[] {
  return pipelines
    .filter((p) => p.id !== 'legacy')
    .map((p) => ({ value: p.id, label: p.name, color: p.color, description: p.is_default ? 'Predefinita' : null }));
}

// Messaggi tecnici (in inglese) che clientStore.updateClient lancia: all'utente
// va una frase in italiano, non il testo grezzo.
const TECH_MESSAGE = /^(Failed to|Could not|Client not found|Update failed|User not|You do not|Not authenticated)/i;

/** Messaggio d'errore in italiano: friendlyDbError se riconosce l'errore, altrimenti la frase di riserva. */
export function chatLeadErrorMessage(error: unknown, fallback: string): string {
  const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (!raw) return fallback;
  if (/permission|not have permission|42501|row-level security/i.test(raw)) return `${fallback}: non hai il permesso per modificarlo`;
  const friendly = friendlyDbError(error);
  if (!friendly || friendly === raw || TECH_MESSAGE.test(friendly)) return fallback;
  return `${fallback}: ${friendly}`;
}
